import {
  ANALYSIS_STATUS_LABELS,
  INTENT_LABELS,
  PRIMARY_MOTIVE_LABELS,
  RATING_ANCHORS,
  RUBRIC_DIMENSIONS,
  computeQualityScore,
  type AnalysisStatus,
  type DimensionEvaluation,
  type Intent,
  type PrimaryMotive,
} from '@convometry/contracts';
import { Ban, Clock, Compass, FileQuestion, Gauge, Loader2, MessageCircleQuestion, PowerOff, RefreshCw, Wallet, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

// Quality score (salesperson quality) is a round pill with a gauge icon and "NN/100".
// Buyer intent / motive are square-cornered outline tags with a compass/question icon.
// The two are never styled alike.

export type ScoreState = AnalysisStatus | 'not_applicable';

export interface ScoreBadgeProps {
  status: ScoreState;
  /** Only rendered when status === 'completed'. */
  score?: number | null;
  provisional?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const STATE_STYLE: Record<Exclude<ScoreState, 'completed'>, { label: string; icon: React.ReactNode; cls: string }> = {
  not_applicable: { label: 'N/A', icon: <Ban aria-hidden />, cls: 'border-dashed border-border-control bg-surface-default text-text-secondary' },
  pending: { label: ANALYSIS_STATUS_LABELS.pending, icon: <Clock aria-hidden />, cls: 'border-border-subtle bg-surface-subtle text-text-secondary' },
  running: { label: ANALYSIS_STATUS_LABELS.running, icon: <Loader2 aria-hidden className="animate-spin" />, cls: 'border-info/25 bg-info-bg text-info' },
  insufficient_evidence: {
    label: ANALYSIS_STATUS_LABELS.insufficient_evidence,
    icon: <FileQuestion aria-hidden />,
    cls: 'border-dashed border-warning/50 bg-warning-bg text-warning',
  },
  failed: { label: ANALYSIS_STATUS_LABELS.failed, icon: <XCircle aria-hidden />, cls: 'border-danger/25 bg-danger-bg text-danger' },
  disabled: { label: ANALYSIS_STATUS_LABELS.disabled, icon: <PowerOff aria-hidden />, cls: 'border-border-subtle bg-surface-subtle text-text-muted' },
  budget_limited: { label: ANALYSIS_STATUS_LABELS.budget_limited, icon: <Wallet aria-hidden />, cls: 'border-warning/25 bg-warning-bg text-warning' },
  stale: { label: ANALYSIS_STATUS_LABELS.stale, icon: <RefreshCw aria-hidden />, cls: 'border-warning/25 bg-warning-bg text-warning' },
};

export function ScoreBadge({ status, score, provisional, size = 'md', className }: ScoreBadgeProps) {
  const base = cn(
    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border font-medium [&_svg]:shrink-0',
    size === 'sm' ? 'px-2 py-0.5 text-helper [&_svg]:size-3.5' : 'px-2.5 py-1 text-body [&_svg]:size-4',
    className,
  );
  if (status === 'completed' && typeof score === 'number') {
    return (
      <span data-state="scored" className={cn(base, 'border-brand-primary/30 bg-brand-tint text-brand-hover')}>
        <Gauge aria-hidden />
        <span className="cm-tabular font-semibold">
          {score}
          <span className="font-normal opacity-80">/100</span>
        </span>
        <span className="sr-only">Calidad de conversación</span>
        {provisional ? <span className="font-normal">· provisional</span> : null}
      </span>
    );
  }
  const s = STATE_STYLE[status === 'completed' ? 'not_applicable' : status];
  return (
    <span data-state={status} className={cn(base, s.cls)}>
      {s.icon}
      {s.label}
    </span>
  );
}

export interface IntentBadgeProps {
  intent: Intent | null | undefined;
  className?: string;
}

/** Buyer readiness (not agent quality). */
export function IntentBadge({ intent, className }: IntentBadgeProps) {
  const label = intent ? INTENT_LABELS[intent] : 'Sin evaluar';
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-[6px] border border-border-control bg-surface-default px-2 py-0.5 text-helper font-medium text-text-primary [&_svg]:size-3.5', className)}>
      <Compass aria-hidden className="text-text-secondary" />
      <span className="text-text-secondary">Intención:</span> {label}
    </span>
  );
}

export interface MotiveBadgeProps {
  motive: PrimaryMotive | null | undefined;
  className?: string;
}

export function MotiveBadge({ motive, className }: MotiveBadgeProps) {
  const label = motive ? PRIMARY_MOTIVE_LABELS[motive] : 'Sin evaluar';
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-[6px] border border-border-control bg-surface-default px-2 py-0.5 text-helper font-medium text-text-primary [&_svg]:size-3.5', className)}>
      <MessageCircleQuestion aria-hidden className="text-text-secondary" />
      <span className="text-text-secondary">Motivo:</span> {label}
    </span>
  );
}

export interface ScoreBreakdownProps {
  dimensions: readonly DimensionEvaluation[];
  /** Called with a message id when an evidence link is activated. */
  onEvidenceClick?: (messageId: string) => void;
  /** Maps message id → short label (e.g. "#12 · 10:42"). */
  evidenceLabel?: (messageId: string) => string;
  /** When set, evidence renders as links (e.g. "#msg-<id>") instead of buttons. */
  evidenceHref?: (messageId: string) => string;
  className?: string;
}

/** Rubric detail: weight, 0–4 rating (or N/A), rationale and evidence per dimension. Total computed in code. */
export function ScoreBreakdown({ dimensions, onEvidenceClick, evidenceLabel, evidenceHref, className }: ScoreBreakdownProps) {
  const total = computeQualityScore(dimensions);
  const applicableWeight = RUBRIC_DIMENSIONS.filter((d) => dimensions.find((x) => x.key === d.key)?.applicable).reduce((s, d) => s + d.weight, 0);
  return (
    <div className={cn('grid gap-3', className)}>
      <ol className="grid gap-3">
        {RUBRIC_DIMENSIONS.map((def) => {
          const d = dimensions.find((x) => x.key === def.key);
          const applicable = !!d?.applicable && d.rating !== null;
          return (
            <li key={def.key} className="rounded-menu border border-border-subtle p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-body font-medium text-text-primary">
                  {def.label} <span className="cm-tabular text-helper font-normal text-text-muted">· peso {def.weight}</span>
                </p>
                {applicable ? (
                  <span className="flex items-center gap-2">
                    <span className="flex gap-0.5" aria-hidden>
                      {[1, 2, 3, 4].map((i) => (
                        <span key={i} className={cn('h-2 w-5 rounded-pill', i <= (d.rating ?? 0) ? 'bg-brand-primary' : 'bg-border-subtle')} />
                      ))}
                    </span>
                    <span className="cm-tabular text-helper font-semibold text-text-primary">
                      {d.rating}/4 <span className="font-normal text-text-secondary">· {RATING_ANCHORS[d.rating ?? 0]}</span>
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-pill border border-dashed border-border-control px-2 py-0.5 text-helper font-medium text-text-secondary">
                    N/A · no aplica
                  </span>
                )}
              </div>
              {d?.rationale ? <p className="mt-1.5 text-body text-text-secondary">{d.rationale}</p> : null}
              {d && d.evidenceIds.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {d.evidenceIds.map((id) => (
                    <EvidenceLink key={id} messageId={id} label={evidenceLabel?.(id)} href={evidenceHref?.(id)} onSelect={onEvidenceClick} />
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="cm-tabular text-helper text-text-secondary">
        Total = 100 × Σ(peso × calificación / 4) / Σ(pesos aplicables = {applicableWeight}) →{' '}
        <span className="font-semibold text-text-primary">{total === null ? 'N/A' : total}</span>. Calculado por el sistema, no por la IA.
      </p>
    </div>
  );
}

export interface EvidenceLinkProps {
  messageId: string;
  label?: string;
  onSelect?: (messageId: string) => void;
  /** Render as a link (in-page anchor) instead of a button. onSelect still runs; default navigation is prevented. */
  href?: string;
  className?: string;
}

/** Jumps to the supporting message in the transcript. */
export function EvidenceLink({ messageId, label, onSelect, href, className }: EvidenceLinkProps) {
  const cls = cn(
    'cm-tabular inline-flex items-center gap-1 rounded-[6px] border border-brand-primary/30 bg-surface-default px-1.5 py-0.5 text-helper font-medium text-brand-primary',
    'hover:bg-brand-tint focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
    className,
  );
  const content = (
    <>
      <span aria-hidden>↗</span>
      <span>{label ?? `Mensaje ${messageId.slice(-6)}`}</span>
      <span className="sr-only">— ir al mensaje de evidencia</span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        onClick={(e) => {
          if (onSelect) {
            e.preventDefault();
            onSelect(messageId);
          }
        }}
        className={cls}
      >
        {content}
      </a>
    );
  }
  return (
    <button type="button" onClick={() => onSelect?.(messageId)} className={cls}>
      {content}
    </button>
  );
}
